<header class="flex w-full flex-col px-4 pb-4 pt-2">
  <div class="flex flex-row items-center gap-4">
    <p-button
      icon="pi pi-arrow-left"
      (onClick)="backToList()"
      [text]="true"
      [rounded]="true"
      pTooltip="Back to list"
    />
    @if (contribution) {
      <div class="flex-1">
        <h1 class="text-2xl font-semibold text-greyscale-900">
          {{ contribution.company.name }} - {{ contribution.user.firstName }} {{ contribution.user.lastName }}
        </h1>
        <p class="text-sm text-greyscale-600">
          {{ contribution.trip.title }} · #{{ getShortId() }}
        </p>
      </div>
      <!-- Document download buttons -->
      @if (isPaid() || (isPendingBankTransfer() && contribution.invoiceS3Key)) {
        <div class="flex items-center gap-2">
          @if (isPaid()) {
            <button
              class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors hover:bg-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
              (click)="downloadCertificate()"
              [disabled]="!contribution.certificateS3Key"
              pTooltip="Download Certificate"
            >
              <i class="pi pi-download"></i>
            </button>
          }
          <button
            class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors hover:bg-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
            (click)="downloadInvoice()"
            [disabled]="!contribution.invoiceS3Key"
            [pTooltip]="contribution.invoiceS3Key ? (isPaid() ? 'Download Invoice' : 'Download Proforma Invoice') : 'Invoice not yet available'"
          >
            <i class="pi pi-file-pdf"></i>
          </button>
        </div>
      }
      <span
        class="rounded-full px-3 py-1 text-sm font-medium"
        [ngClass]="getStatusClasses(contribution.status)"
      >
        {{ getStatusLabel(contribution.status) }}
      </span>
    } @else {
      <h1 class="text-2xl font-semibold text-greyscale-900">Loading...</h1>
    }
  </div>
</header>

<main class="p-4">
  @if (loading) {
    <div class="flex h-64 items-center justify-center">
      <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
    </div>
  } @else if (contribution) {
    <div class="flex flex-col gap-4">
      <!-- Main 2-column layout -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <!-- LEFT COLUMN: Details + Client + Amount -->
        <div class="space-y-3 lg:col-span-3">
          <!-- Details & Client Card (merged) -->
          <div class="rounded-lg border border-gray-200 bg-white p-3">
            <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <p class="text-xs text-gray-500">Trip</p>
                <p class="font-medium text-gray-900">{{ contribution.trip.title }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Travelers</p>
                <p class="font-medium text-gray-900">{{ contribution.trip.travellers }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Coverage</p>
                <p class="font-medium text-gray-900">{{ contribution.globalPercentage }}%</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">CO2</p>
                <p class="font-medium text-gray-900">{{ getTotalCO2() | number:'1.2-2' }} kg</p>
              </div>
            </div>
            <div class="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-100 pt-2 text-sm sm:grid-cols-4">
              <div>
                <p class="text-xs text-gray-500">Client</p>
                <p class="font-medium text-gray-900">{{ contribution.user.firstName }} {{ contribution.user.lastName }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Email</p>
                <p class="font-medium text-gray-900">{{ getUserEmail() }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Company</p>
                <p class="font-medium text-gray-900">{{ contribution.company.name }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Created</p>
                <p class="font-medium text-gray-900">{{ formatDate(contribution.createdAt) }}</p>
              </div>
            </div>
            <div class="mt-2 border-t border-gray-100 pt-2 text-xs">
              <span class="text-gray-500">ID:</span>
              <span class="ml-1 font-mono text-gray-700">{{ contribution.id }}</span>
            </div>
          </div>

          <!-- Amount Card (compact) -->
          <div class="rounded-lg border border-gray-200 bg-white p-3">
            <div class="flex items-center justify-between gap-4 text-sm">
              <div class="flex gap-4">
                <div>
                  <span class="text-xs text-gray-500">Subtotal</span>
                  <span class="ml-1 font-medium">{{ formatAmount(getSubtotal()) }}</span>
                </div>
                <div>
                  <span class="text-xs text-gray-500">VAT 21%</span>
                  <span class="ml-1 font-medium">{{ formatAmount(getVatAmount()) }}</span>
                </div>
              </div>
              <div class="text-right">
                <span class="text-xs text-gray-500">Total</span>
                <span class="ml-2 text-lg font-bold text-primary-600">{{ formatAmount(contribution.totalAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN: Contextual Action Card + Documents -->
        <div class="space-y-3 lg:col-span-2 lg:sticky lg:top-4 lg:self-start">
          <!-- Contextual Card based on status -->
          @if (isPaid()) {
            <!-- PAID STATE -->
            <div class="overflow-hidden rounded-lg border border-validation-200 bg-white">
              <div class="flex items-center gap-2 bg-validation-50 px-3 py-2">
                <i class="pi pi-check-circle text-validation-600"></i>
                <span class="font-semibold text-validation-700">Payment Confirmed</span>
              </div>
              <div class="space-y-2 p-3 text-sm">
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Method</span>
                  <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium">
                    {{ getPaymentMethodLabel(contribution.paymentMethod!) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Paid</span>
                  <span class="font-medium">{{ formatDate(contribution.paidAt) }}</span>
                </div>
                @if (contribution.stripePaymentIntentId) {
                  <div class="flex items-center justify-between">
                    <span class="text-gray-500">Stripe</span>
                    <div class="flex items-center gap-1">
                      <span class="font-mono text-xs">{{ contribution.stripePaymentIntentId.substring(0, 20) }}...</span>
                      <button class="text-gray-400 hover:text-gray-600" (click)="copyToClipboard(contribution.stripePaymentIntentId)">
                        <i class="pi pi-copy text-xs"></i>
                      </button>
                    </div>
                  </div>
                }
                @if (contribution.paidBy) {
                  <div class="flex justify-between">
                    <span class="text-gray-500">By</span>
                    <span class="font-medium">{{ contribution.paidBy }}</span>
                  </div>
                }
              </div>
            </div>
            <!-- Odoo Sync Failed Warning -->
            @if (isOdooSyncFailed()) {
              <div class="overflow-hidden rounded-lg border border-error-200 bg-white">
                <div class="flex items-center gap-2 bg-error-50 px-3 py-2">
                  <i class="pi pi-exclamation-triangle text-error-600"></i>
                  <span class="font-semibold text-error-700">Invoice Sync Failed</span>
                </div>
                <div class="space-y-2 p-3 text-sm">
                  <p class="text-gray-600">
                    The invoice could not be synced to Odoo. The user has received their certificate but not their invoice.
                  </p>
                  <p-button
                    label="Retry Sync"
                    icon="pi pi-refresh"
                    (onClick)="retryOdooSync()"
                    [loading]="retryingOdooSync"
                    styleClass="w-full"
                    size="small"
                    severity="danger"
                  />
                </div>
              </div>
            }
          } @else if (isPendingBankTransfer()) {
            <!-- PENDING BANK TRANSFER: Action Required -->
            <div class="overflow-hidden rounded-lg border border-amber-200 bg-white">
              <div class="flex items-center gap-2 bg-amber-50 px-3 py-2">
                <i class="pi pi-clock text-amber-600"></i>
                <span class="font-semibold text-amber-700">Action Required</span>
              </div>
              <div class="space-y-2 p-3 text-sm">
                @if (contribution.bankDetails) {
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500">IBAN</span>
                    <div class="flex items-center gap-1">
                      <span class="font-mono text-xs">{{ contribution.bankDetails.iban }}</span>
                      <button class="text-gray-400 hover:text-gray-600" (click)="copyToClipboard(contribution.bankDetails.iban)">
                        <i class="pi pi-copy text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500">BIC</span>
                    <div class="flex items-center gap-1">
                      <span class="font-mono text-xs">{{ contribution.bankDetails.bic }}</span>
                      <button class="text-gray-400 hover:text-gray-600" (click)="copyToClipboard(contribution.bankDetails.bic)">
                        <i class="pi pi-copy text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <!-- Reference - Highlighted -->
                  <div class="rounded border border-primary-200 bg-primary-50 p-2">
                    <div class="flex items-center justify-between">
                      <div>
                        <p class="text-xs text-primary-600">Reference</p>
                        <p class="font-mono font-bold text-primary-800">{{ contribution.bankDetails.reference }}</p>
                      </div>
                      <button class="rounded bg-primary-600 p-1.5 text-white hover:bg-primary-700" (click)="copyToClipboard(contribution.bankDetails.reference)">
                        <i class="pi pi-copy text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500">Amount</span>
                    <span class="font-bold">{{ contribution.bankDetails.amount | number:'1.2-2' }} EUR</span>
                  </div>
                }
                <p-button
                  label="Mark as Paid"
                  icon="pi pi-check"
                  (onClick)="markAsPaid()"
                  [loading]="markingAsPaid"
                  styleClass="w-full"
                  size="small"
                />
              </div>
            </div>
          } @else if (contribution.status === ContributionStatus.PENDING_PAYMENT) {
            <!-- PENDING CARD PAYMENT -->
            <div class="overflow-hidden rounded-lg border border-amber-200 bg-white">
              <div class="flex items-center gap-2 bg-amber-50 px-3 py-2">
                <i class="pi pi-spin pi-spinner text-amber-600"></i>
                <span class="font-semibold text-amber-700">Processing</span>
              </div>
              <div class="p-3 text-sm text-gray-600">
                Card payment processing...
              </div>
            </div>
          } @else if (contribution.status === ContributionStatus.FAILED) {
            <div class="overflow-hidden rounded-lg border border-error-200 bg-white">
              <div class="flex items-center gap-2 bg-error-50 px-3 py-2">
                <i class="pi pi-times-circle text-error-600"></i>
                <span class="font-semibold text-error-700">Failed</span>
              </div>
              <div class="p-3 text-sm text-gray-600">Payment declined.</div>
            </div>
          } @else if (contribution.status === ContributionStatus.REFUNDED) {
            <div class="overflow-hidden rounded-lg border border-primary-200 bg-white">
              <div class="flex items-center gap-2 bg-primary-50 px-3 py-2">
                <i class="pi pi-replay text-primary-600"></i>
                <span class="font-semibold text-primary-700">Refunded</span>
              </div>
            </div>
          } @else {
            <div class="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div class="flex items-center gap-2 bg-gray-50 px-3 py-2">
                <i class="pi pi-ban text-gray-500"></i>
                <span class="font-semibold text-gray-700">Canceled</span>
              </div>
            </div>
          }

        </div>
      </div>

      <!-- Projects Table (compact) -->
      <div class="rounded-lg border border-gray-200 bg-white p-3">
        <p-table [value]="contribution.projectContributions" styleClass="p-datatable-sm">
          <ng-template #header>
            <tr class="text-xs">
              <th>Project</th>
              <th class="text-right">%</th>
              <th class="text-right">CO2</th>
              <th class="text-right">Amount</th>
              <th class="text-right">€/t</th>
            </tr>
          </ng-template>
          <ng-template #body let-pc>
            <tr class="text-sm">
              <td class="font-medium">{{ pc.projectName }}</td>
              <td class="text-right">{{ pc.percentage }}%</td>
              <td class="text-right">{{ pc.co2Compensated | number:'1.2-2' }} kg</td>
              <td class="text-right">{{ formatAmount(pc.amount) }}</td>
              <td class="text-right">{{ formatAmount(pc.pricePerTonne) }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <!-- Admin Notes (compact) -->
      <div class="rounded-lg border border-gray-200 bg-white p-3">
        @if (contribution.adminNotes) {
          <div class="mb-3 space-y-2">
            @for (note of contribution.adminNotes.split('\n\n'); track note) {
              @if (note.trim()) {
                <div class="border-l-2 border-gray-200 pl-2 text-xs text-gray-600">{{ note.trim() }}</div>
              }
            }
          </div>
        }
        <div class="flex gap-2">
          <input
            type="text"
            [(ngModel)]="newNotes"
            placeholder="Add note..."
            class="flex-1 rounded border border-gray-200 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
            [maxLength]="2000"
            (keydown.enter)="saveAdminNotes()"
          />
          <p-button
            icon="pi pi-send"
            (onClick)="saveAdminNotes()"
            [disabled]="!newNotes.trim() || savingNotes"
            [loading]="savingNotes"
            size="small"
          />
        </div>
      </div>
    </div>
  }
</main>

<p-toast />
<p-confirmDialog rejectButtonStyleClass="p-button-outlined" />
